import { Button, Card, Modal, Spinner } from "react-bootstrap";
import { BsToggle2Off, BsToggle2On } from "react-icons/bs";
import { Plan, ePlanType } from "../../../../@types/Billing/Plan";
import { useState, useEffect } from "react";
import usePlan from "../../../../hooks/usePlan";
import { isEmpty } from "lodash";
import { availablePlans } from "./data";
import { CgDanger } from "react-icons/cg";
import useBillingContext, {
  eBillingStep,
} from "../../../../context/BillingContext";
import { useHistory, useLocation } from "react-router-dom";
import useSubscription from "../../../../hooks/useSubscription";
import { useCustomer } from "../../../../hooks/useCustomer";
import { AiOutlineCloseCircle } from "react-icons/ai";

export default function ShowPlans() {
  const [planType, setPlanType] = useState<ePlanType>(ePlanType.MONTHLY);
  const [displayablePlans, setDisplayablePlans] = useState<Plan[]>([]);
  const [localRelationedPlans, setLocalRelationedPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const { getPlans } = usePlan();
  const { isUserPremiumUncached } = useSubscription();
  const { setStep, setSelectedPlan } = useBillingContext();
  const { isTrialEligible } = useCustomer();
  const location = useLocation();
  const history = useHistory();

  function handlePlanSelect(plan: Plan) {
    setSelectedPlan(plan);
    setStep(eBillingStep.CREATE_CUSTOMER);
  }

  /**
   * Determine plan from URL param and set on context whether applicable
   */
  function determineParamPlan() {
    const planId = new URLSearchParams(location.search).get("planId");

    if (planId) {
      const chosenPlan = availablePlans.find(
        (plan) => plan.identifier === planId
      );

      if (chosenPlan) {
        setSelectedPlan(chosenPlan);
        setStep(eBillingStep.CREATE_CUSTOMER);
      }
    }
  }

  async function fillFilteredPlans() {
    setIsLoading(true);
    setIsError(false);
    const [storedPlans, isAlreadyPremium, isUserTrialable] = await Promise.all([
      getPlans(),
      isUserPremiumUncached(),
      isTrialEligible(),
    ]);

    if (isAlreadyPremium) {
      history.push("/dashboard");
    }

    if (!isEmpty(storedPlans)) {
      const localPlans: Plan[] = [];

      storedPlans.forEach((storedPlan) => {
        const localPlan = availablePlans.find(
          (availablePlan) => availablePlan.identifier === storedPlan.identifier
        );

        if (localPlan) {
          if (localPlan.identifier === "bronze_mensal" && isUserTrialable) {
            localPlan.canTrial = true;
          }

          localPlan.priceId = storedPlan.price_id;
          localPlans.push(localPlan);
        }
      });
      setLocalRelationedPlans(localPlans);

      const newDisplayablePlansState = localPlans.filter(
        (plan) => plan.type === planType
      );
      setDisplayablePlans(newDisplayablePlansState);
    } else {
      setIsError(true);
    }

    setIsLoading(false);
    return determineParamPlan();
  }

  useEffect(() => {
    fillFilteredPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update plans on switch between expirations (monthly or annual)
  useEffect(() => {
    const newDisplayablePlans = localRelationedPlans.filter(
      (plan) => plan.type === planType
    );
    setDisplayablePlans(newDisplayablePlans);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planType]);

  function handlePlanTypeChange() {
    if (planType === ePlanType.MONTHLY) {
      setPlanType(ePlanType.ANNUALY);
    } else {
      setPlanType(ePlanType.MONTHLY);
    }
  }

  function getSwitch() {
    if (planType === ePlanType.MONTHLY) {
      return (
        <BsToggle2Off
          cursor={"pointer"}
          style={{ marginTop: -6, marginBottom: 10 }}
          size={30}
          onClick={handlePlanTypeChange}
        />
      );
    } else {
      return (
        <BsToggle2On
          cursor={"pointer"}
          style={{ marginTop: -6, marginBottom: 10 }}
          size={30}
          onClick={handlePlanTypeChange}
        />
      );
    }
  }

  function getErrorCard() {
    return (
      <Card className="pd-10 rounded-button">
        <div className="row">
          <div className="col-12 centered-flex">
            <CgDanger size={35} />
          </div>

          <div className="col-12 centered-flex">
            <h2>Ops</h2>
          </div>

          <div className="col-12 centered-flex">
            <p className="tx-center">
              Ocorreu um erro desconhecido ao buscar planos disponíveis, por
              favor, tente novamente.
            </p>
          </div>

          <div className="col-12 centered-flex">
            <Button
              className="rounded-button"
              variant="primary"
              onClick={fillFilteredPlans}
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Modal
      size="lg"
      fullscreen={true}
      show={true}
      dialogClassName="plans-modal-dialog"
      contentClassName="empty-background"
    >
      <div className="container mw-95">
        <div className="row">
          <div
            className="col-lg-12 shadow-lg d-flex align-items-center rounded-3 rounded-start-0 "
            style={{
              backgroundColor: "#F6F7F9",
              borderRadius: 10,
            }}
          >
            <div className="container my-auto py-4 pd-l-10 pd-r-10">
              <div className="float-right">
                <AiOutlineCloseCircle
                  color="var(--text-1)"
                  cursor={"pointer"}
                  size={25}
                  onClick={() => {
                    history.push("/dashboard");
                  }}
                />
              </div>

              <div className="row">
                <div className="col-12 mx-auto">
                  <div className="d-flex align-itens-center justify-content-center">
                    <h3 className="text-black text-center mb-3">
                      Selecione seu plano
                    </h3>
                  </div>

                  <div className="row">
                    {!isLoading && !isError && (
                      <div className="col-12">
                        <div className="d-flex align-itens-center justify-content-center">
                          <span
                            className={`mg-r-10 not-selectable ${planType === ePlanType.MONTHLY && "tx-primary"
                              }`}
                            style={{ cursor: "default" }}
                          >
                            Mensal{" "}
                          </span>

                          {getSwitch()}
                          <span
                            className={`mg-l-10 not-selectable ${planType === ePlanType.ANNUALY && "tx-primary"
                              }`}
                            style={{ cursor: "default" }}
                          >
                            {" "}
                            Anual
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="d-flex align-itens-center justify-content-center">
                    {isLoading && (
                      <div className="mg-t-20 mg-b-20">
                        <Spinner animation="border"></Spinner>
                      </div>
                    )}
                  </div>

                  {isError && getErrorCard()}

                  <div className="row">
                    {displayablePlans.map((plan, i) => {
                      return (
                        <div
                          key={i}
                          className="col-12 col-md-6 col-lg-4 card-group"
                        >
                          {plan.canTrial
                            ? TrialPlanCard(plan, handlePlanSelect)
                            : PlanCard(plan, handlePlanSelect)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function PlanCard(plan: Plan, clickButtonAction: CallableFunction) {
  return (
    <Card
      className="shadow"
      style={{ borderRadius: 15, padding: 15, marginTop: 10 }}
    >
      <div className="centered-flex">{plan.icon.element}</div>

      <p className="tx-18 tx-roboto tx-medium  text-center">{plan.title}</p>

      <p
        style={{ marginTop: -15 }}
        className="tx-30 tx-roboto tx-medium  text-center "
      >
        <span className="tx-16 tx-light ">R$ </span>
        {plan.price}
        <span className="tx-16 tx-light">
          {plan.type === ePlanType.MONTHLY ? "/Mês" : "/Ano"}
        </span>
      </p>

      <div className="centered-flex">
        <ul className="list-custom mb-20px">
          {plan.features.map((feature, i) => {
            return <li key={i}>{feature}</li>;
          })}
        </ul>
      </div>

      <div className="centered-flex">
        <Button
          style={{ borderRadius: 15 }}
          variant="primary"
          onClick={() => clickButtonAction(plan)}
        >
          Selecionar
        </Button>
      </div>
    </Card>
  );
}

function TrialPlanCard(plan: Plan, clickButtonAction: CallableFunction) {
  return (
    <Card
      className="shadow"
      style={{ borderRadius: 15, padding: 15, marginTop: 10 }}
    >
      <div className="centered-flex">{plan.icon.element}</div>

      <p className="tx-18 tx-roboto tx-medium  text-center">{plan.title}</p>

      <p className="tx-14 tx-light tx-medium text-center tx-gray mg-b-10" style={{
        textDecoration: 'line-through'
      }}>
        R$ 109,90/Mês
      </p>
      <p
        style={{ marginTop: -15 }}
        className="tx-30 tx-roboto tx-medium text-center mg-0 tx-success"
      >
        <span className="tx-16 tx-light ">R$ </span>
        {plan.price}
        <span className="tx-16 tx-light">
          {plan.type === ePlanType.MONTHLY ? "/Mês" : "/Ano"}
        </span>
      </p>

      <p className="tx-14 tx-light tx-medium text-center tx-primary mg-0">
        OFERTA POR TEMPO LIMITADO: 55% DE DESCONTO.
      </p>

      <div className="centered-flex">
        <ul className="list-custom mb-20px">
          {plan.features.map((feature, i) => {
            return <li key={i}>{feature}</li>;
          })}
        </ul>
      </div>

      <div className="centered-flex mg-t-5">
        <Button
          style={{ borderRadius: 15 }}
          variant="primary"
          onClick={() => clickButtonAction(plan)}
        >
          Selecionar
        </Button>
      </div>
    </Card>
  );
}
